/**
 * API service layer — all fetch calls to the backend Azure Functions API.
 *
 * Every exported function accepts a `token` (idToken string from MSAL) and
 * builds the Authorization header. The base URL is empty in production (Azure
 * Static Web Apps routes /api/* to the linked Function app), and set to the
 * dev API URL in .env.local via VITE_API_BASE_URL.
 */

import type {
  Allocation,
  AuditLog,
  BhsdUser,
  DashboardData,
  Document,
  FiscalYear,
  FundPool,
  Payee,
  PaymentMethod,
  PaymentRequest,
  Project,
  User,
} from '../types';

const BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';

function headers(token: string): HeadersInit {
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const text = await res.text();
      try {
        const body = JSON.parse(text) as { error?: string };
        if (body.error) msg = body.error;
      } catch {
        if (text) msg = text.slice(0, 300);
      }
    } catch {
      // ignore
    }
    throw new Error(msg);
  }
  return res.json() as Promise<T>;
}

// ─── Users ────────────────────────────────────────────────────────────────────

export async function getMe(token: string): Promise<User> {
  const res = await fetch(`${BASE}/api/users/me`, { headers: headers(token) });
  return handleResponse<User>(res);
}

// ─── Dashboard ────────────────────────────────────────────────────────────────

export async function getDashboard(token: string): Promise<DashboardData> {
  const res = await fetch(`${BASE}/api/dashboard`, { headers: headers(token) });
  return handleResponse<DashboardData>(res);
}

// ─── Payment Requests ─────────────────────────────────────────────────────────

export interface ListRequestsParams {
  search?: string;
  status?: string;
  payee_id?: number;
  fiscalYearId?: number;
  page?: number;
  pageSize?: number;
}

export interface RequestsListResponse {
  data: PaymentRequest[];
  total: number;
  page: number;
  pageSize: number;
}

export async function getPaymentRequests(
  token: string,
  params: ListRequestsParams = {},
): Promise<RequestsListResponse> {
  const qs = new URLSearchParams();
  if (params.search) qs.set('search', params.search);
  if (params.status) qs.set('status', params.status);
  if (params.payee_id) qs.set('payee_id', String(params.payee_id));
  if (params.fiscalYearId) qs.set('fiscalYearId', String(params.fiscalYearId));
  if (params.page) qs.set('page', String(params.page));
  if (params.pageSize) qs.set('pageSize', String(params.pageSize));
  const url = `${BASE}/api/payment-requests${qs.toString() ? `?${qs}` : ''}`;
  const res = await fetch(url, { headers: headers(token) });
  return handleResponse<RequestsListResponse>(res);
}

export async function getPaymentRequest(token: string, id: number): Promise<PaymentRequest> {
  const res = await fetch(`${BASE}/api/payment-requests/${id}`, { headers: headers(token) });
  return handleResponse<PaymentRequest>(res);
}

export interface CreateRequestBody {
  allocation_id: number;
  payee_id: number;
  payment_method_id: number;
  amount: number;
  bhsd_approver_notes: string | null;
}

export async function createPaymentRequest(
  token: string,
  body: CreateRequestBody,
): Promise<{ payment_request_id: number; request_number: string }> {
  const res = await fetch(`${BASE}/api/payment-requests`, {
    method: 'POST',
    headers: headers(token),
    body: JSON.stringify(body),
  });
  return handleResponse(res);
}

export interface EditPaymentRequestFields {
  amount?: number;
  payee_id?: number;
  allocation_id?: number;
  bhsd_approver_notes?: string | null;
  requested_by?: number;
  request_date?: string; // YYYY-MM-DD
}

export async function editPaymentRequest(
  token: string,
  id: number,
  fields: EditPaymentRequestFields,
): Promise<void> {
  const res = await fetch(`${BASE}/api/payment-requests/${id}`, {
    method: 'PATCH',
    headers: headers(token),
    body: JSON.stringify(fields),
  });
  await handleResponse(res);
}

export async function approveBhsdRequest(token: string, id: number): Promise<void> {
  const res = await fetch(`${BASE}/api/payment-requests/${id}/approve-bhsd`, {
    method: 'POST',
    headers: headers(token),
  });
  await handleResponse(res);
}

export async function approveOnyxRequest(token: string, id: number): Promise<void> {
  const res = await fetch(`${BASE}/api/payment-requests/${id}/approve-onyx`, {
    method: 'POST',
    headers: headers(token),
  });
  await handleResponse(res);
}

export async function rejectRequest(
  token: string,
  id: number,
  rejection_reason: string,
): Promise<void> {
  const res = await fetch(`${BASE}/api/payment-requests/${id}/reject`, {
    method: 'POST',
    headers: headers(token),
    body: JSON.stringify({ rejection_reason }),
  });
  await handleResponse(res);
}

export async function confirmCreditCardPayment(token: string, id: number): Promise<void> {
  const res = await fetch(`${BASE}/api/payment-requests/${id}/confirm-payment`, {
    method: 'POST',
    headers: headers(token),
  });
  await handleResponse(res);
}

// ─── Audit Log ────────────────────────────────────────────────────────────────

export async function getAuditLog(token: string, paymentRequestId: number): Promise<AuditLog[]> {
  const res = await fetch(
    `${BASE}/api/payment-requests/${paymentRequestId}/audit`,
    { headers: headers(token) },
  );
  return handleResponse<AuditLog[]>(res);
}

// ─── Payees ───────────────────────────────────────────────────────────────────

export interface PayeesListResponse {
  data: Payee[];
  total: number;
}

export async function getPayees(token: string, search?: string): Promise<PayeesListResponse> {
  const qs = search ? `?search=${encodeURIComponent(search)}` : '';
  const res = await fetch(`${BASE}/api/payees${qs}`, { headers: headers(token) });
  return handleResponse<PayeesListResponse>(res);
}

export async function getPayee(token: string, id: number): Promise<Payee> {
  const res = await fetch(`${BASE}/api/payees/${id}`, { headers: headers(token) });
  return handleResponse<Payee>(res);
}

export interface CreatePayeeBody {
  payee_name: string;
  email: string | null;
  phone: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state_code: string | null;
  zip_code: string | null;
  has_sow: boolean;
  contract_required: boolean;
  preferred_method_id: number | null;
}

export async function createPayee(
  token: string,
  body: CreatePayeeBody,
): Promise<{ payee_id: number }> {
  const res = await fetch(`${BASE}/api/payees`, {
    method: 'POST',
    headers: headers(token),
    body: JSON.stringify(body),
  });
  return handleResponse(res);
}

export async function updatePayee(
  token: string,
  id: number,
  body: Partial<CreatePayeeBody>,
): Promise<void> {
  const res = await fetch(`${BASE}/api/payees/${id}`, {
    method: 'PATCH',
    headers: headers(token),
    body: JSON.stringify(body),
  });
  await handleResponse(res);
}

// ─── Allocations ──────────────────────────────────────────────────────────────

export interface AllocationsListResponse {
  data: Allocation[];
  total_allocation: number;
  total_paid: number;
}

export interface GetAllocationsOptions {
  fiscalYearId?: number;
  excludeClosed?: boolean;
}

export async function getAllocations(
  token: string,
  opts: GetAllocationsOptions = {},
): Promise<AllocationsListResponse> {
  const qs = new URLSearchParams();
  if (opts.fiscalYearId) qs.set('fiscalYearId', String(opts.fiscalYearId));
  if (opts.excludeClosed) qs.set('excludeClosed', 'true');
  const url = `${BASE}/api/allocations${qs.toString() ? `?${qs}` : ''}`;
  const res = await fetch(url, { headers: headers(token) });
  return handleResponse<AllocationsListResponse>(res);
}

export interface CreateAllocationBody {
  fund_pool_id: number;
  project_id: number;
  fiscal_year_id: number;
  allocated_amount: number;
}

export async function createAllocation(
  token: string,
  body: CreateAllocationBody,
): Promise<{ allocation_id: number }> {
  const res = await fetch(`${BASE}/api/allocations`, {
    method: 'POST',
    headers: headers(token),
    body: JSON.stringify(body),
  });
  return handleResponse(res);
}

// ─── Reference Data ───────────────────────────────────────────────────────────

export async function getFundPools(token: string): Promise<FundPool[]> {
  const res = await fetch(`${BASE}/api/fund-pools`, { headers: headers(token) });
  const body = await handleResponse<{ data: FundPool[] } | FundPool[]>(res);
  return Array.isArray(body) ? body : (body as { data: FundPool[] }).data;
}

export async function getProjects(token: string): Promise<Project[]> {
  const res = await fetch(`${BASE}/api/projects`, { headers: headers(token) });
  const body = await handleResponse<{ data: Project[] } | Project[]>(res);
  return Array.isArray(body) ? body : (body as { data: Project[] }).data;
}

export async function getPaymentMethods(token: string): Promise<PaymentMethod[]> {
  const res = await fetch(`${BASE}/api/payment-methods`, { headers: headers(token) });
  const body = await handleResponse<{ data: PaymentMethod[] }>(res);
  return body.data;
}

export async function getBhsdUsers(token: string): Promise<BhsdUser[]> {
  const res = await fetch(`${BASE}/api/users/bhsd`, { headers: headers(token) });
  const body = await handleResponse<{ data: BhsdUser[] }>(res);
  return body.data;
}

export async function getFiscalYears(
  token: string,
  opts: { excludeClosed?: boolean } = {},
): Promise<FiscalYear[]> {
  const qs = opts.excludeClosed ? '?excludeClosed=true' : '';
  const res = await fetch(`${BASE}/api/fiscal-years${qs}`, { headers: headers(token) });
  const body = await handleResponse<{ data: FiscalYear[] } | FiscalYear[]>(res);
  return Array.isArray(body) ? body : (body as { data: FiscalYear[] }).data;
}

// ─── Documents ────────────────────────────────────────────────────────────────

export interface DocumentsResponse {
  data: Document[];
}

export async function getDocuments(
  token: string,
  paymentRequestId?: number,
  payeeId?: number,
): Promise<Document[]> {
  const qs = new URLSearchParams();
  if (paymentRequestId) qs.set('payment_request_id', String(paymentRequestId));
  if (payeeId) qs.set('payee_id', String(payeeId));
  const url = `${BASE}/api/documents${qs.toString() ? `?${qs}` : ''}`;
  const res = await fetch(url, { headers: headers(token) });
  const body = await handleResponse<DocumentsResponse | Document[]>(res);
  return Array.isArray(body) ? body : (body as DocumentsResponse).data;
}

export async function uploadDocument(
  token: string,
  formData: FormData,
): Promise<Document> {
  const res = await fetch(`${BASE}/api/documents`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` }, // no Content-Type — browser sets multipart boundary
    body: formData,
  });
  return handleResponse<Document>(res);
}

export async function deleteDocument(token: string, documentId: number): Promise<void> {
  const res = await fetch(`${BASE}/api/documents/${documentId}`, {
    method: 'DELETE',
    headers: headers(token),
  });
  await handleResponse(res);
}

// ─── QBO ──────────────────────────────────────────────────────────────────────

export async function getQboStatus(token: string): Promise<{ connected: boolean }> {
  const res = await fetch(`${BASE}/api/qbo/status`, { headers: headers(token) });
  return handleResponse<{ connected: boolean }>(res);
}

export async function getQboConnectUrl(token: string): Promise<{ url: string }> {
  const res = await fetch(`${BASE}/api/qbo/connect`, { headers: headers(token) });
  return handleResponse<{ url: string }>(res);
}
